import { useLayoutEffect, useRef, useState } from "react";

import type { Table } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";

import { LockIcon } from "@/assets/icons";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table as TableUI,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import Tooltip from "@/components/ui/tooltip";
import { TOOLTIP_TRIGGER_LENGTH } from "@/types/common";
import { DatasetDataRowValue } from "@/types/dataset";
import { formatRenderValue } from "@/utils/string";

import { twMerge } from "tailwind-merge";

import { ExtendedColumnDef } from "../datasetTable/columns";

interface TableDataProps<TData> {
  table: Table<TData>;
  emptyDataMessage?: string | React.ReactNode;
  isLocked?: boolean;
}

export default function TableData<TData>({
  table,
  emptyDataMessage,
  isLocked
}: Readonly<TableDataProps<TData>>) {
  const rows = table.getRowModel().rows;
  const sixthRowRef = useRef<HTMLTableRowElement | null>(null);
  const [overlayTop, setOverlayTop] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (sixthRowRef.current) {
      const { top } = sixthRowRef.current.getBoundingClientRect();
      const tableTop =
        sixthRowRef.current.closest("table")?.getBoundingClientRect().top ?? 0;
      setOverlayTop(top - tableTop); // distance from table top
    }
  }, [rows.length]);

  const checkIsEmpty = (value: unknown): boolean =>
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "number" && Number.isNaN(value)) ||
    (typeof value === "object" &&
      value !== null &&
      Object.keys(value).length === 0);

  return (
    <div className="relative h-full overflow-hidden">
      <ScrollArea className="h-full border border-gainsboro rounded-xl">
        <TableUI
          className="relative table-fixed border-separate border-spacing-0"
          role="grid"
        >
          {isLocked && rows.length > 5 && overlayTop !== null && (
            <div
              data-testid="blur-overlay"
              className="absolute left-0 right-0 bottom-0 flex flex-col items-center justify-center backdrop-blur-sm bg-white/50"
              style={{
                top: overlayTop
              }}
            >
              <LockIcon className="w-12 h-12 text-gray-700 mb-2" />
              <p className="text-lg font-semibold text-gray-800 text-center">
                Unlock to view the full dataset list
              </p>
            </div>
          )}

          <TableHeader>
            {table.getHeaderGroups().map((headerGroup, index) => (
              <TableRow
                key={`${headerGroup.id}-${index}`}
                className="border-b-0"
              >
                {headerGroup.headers.map((header, index) => {
                  const isFirstHeader = header.index === 0;
                  const isLastHeader =
                    header.index === headerGroup.headers.length - 1;

                  return (
                    <TableHead
                      key={`${header.id}-${index}`}
                      className={twMerge(
                        "h-14 bg-white-smoke py-2 px-3 w-[200px] !border-b",
                        isFirstHeader && "border-l-0 rounded-tl-lg",
                        isLastHeader && "border-r-0 rounded-tr-lg"
                      )}
                      scope="col"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="max-h-[522px]">
            {rows?.length ? (
              rows.map((row, rowIndex) => {
                const visibleCells = row.getVisibleCells();
                const isSelected = row.getIsSelected();
                const firstCellId = visibleCells[0]?.id;
                const lastCellId = visibleCells[visibleCells.length - 1]?.id;

                return (
                  <TableRow
                    key={`${row.id}-${rowIndex}`}
                    data-state={isSelected && "selected"}
                    aria-selected={isSelected}
                    ref={rowIndex === 5 ? sixthRowRef : null}
                  >
                    {visibleCells.map((cell, index) => {
                      const value = cell.getValue() as DatasetDataRowValue;
                      const isEmpty = checkIsEmpty(value);
                      const isFirstCell = firstCellId === cell.id;
                      const isLastCell = lastCellId === cell.id;

                      const isTooltipDisabled = !(
                        typeof value === "string" &&
                        value.length > TOOLTIP_TRIGGER_LENGTH
                      );
                      const columnType = (
                        cell.column.columnDef as ExtendedColumnDef<
                          TData,
                          unknown
                        >
                      )?.type;

                      const formatedValue = columnType
                        ? formatRenderValue({
                            type: columnType,
                            value: value
                          })
                        : value;

                      return (
                        <TableCell
                          key={`${cell.id}-${index}`}
                          className={twMerge(
                            "py-2 px-3 !border-b",
                            isEmpty && "bg-white-smoke",
                            isFirstCell && "border-l-0",
                            isLastCell && "border-r-0"
                          )}
                        >
                          <Tooltip
                            content={formatedValue}
                            position="bottom-center"
                            className="z-[1000] text-left w-fit bg-neutral-800 max-w-screen-sm break-words whitespace-pre-wrap"
                            arrowClassName="fill-neutral-800"
                            disable={isTooltipDisabled}
                          >
                            <div
                              className={twMerge(
                                "max-w-[200px] line-clamp-2 text-ellipsis",
                                !isTooltipDisabled && "cursor-pointer"
                              )}
                            >
                              {formatedValue}
                            </div>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="text-center h-[calc(100vh-500px)]"
                >
                  {emptyDataMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </TableUI>
      </ScrollArea>
    </div>
  );
}
